import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
} from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-slidedown/lib/slidedown.css";
import { RRule, rrulestr } from "rrule";
import { coreApi } from "../../../api/calls";
import throwError from "../../helpers/helpers";
import PartnerSelect from "../PartnerSelect";
import { Link } from "react-router-dom";
import BiweeklyTypeSelect from "../../Scheduler/BiweeklyTypeSelect";
import { IsBiweeklyBuildingUnit } from "../../../helpers";
import {getDays} from "../../helpers/rrule";

/**
 * Mass create form for invocies during the month.
 *
 * @component
 * @alias MassInvoiceDuringMonthForm
 * @property {object} partner
 * @returns  <Button /> which opens a <Modal />
 */
const MassInvoiceDuringMonthForm = ({ partner, selectedBuildingUnits: selectedBuildingUnitIds }) => {
  // Ant design components
  const { Option } = Select;

  // Translations
  const { t } = useTranslation();

  const [internalPartner, setInternalPartner] = useState(partner || null);

  // Array with days in week
  const days = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

  // States
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [invoiceForm] = Form.useForm();
  const [buildingUnits, setBuildingUnits] = useState([]);
  const [selectedBuildingUnits, setSelectedBuildingUnits] = useState(selectedBuildingUnitIds || []);
  const [buildingUnitsList, setBuildingUnitsList] = useState([]);
  const [buildingUnitsSummary, setBuildingUnitsSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [biweeklyType, setBiweeklyType] = useState([]);
  const searchTermMinLength = 3;

  const fetchBuildingUnits = useCallback(async ({ term = null, ids = [] } = {}) => {
    const params = {
      term: term?.length > searchTermMinLength ? term : null,
      building_unit_ids: ids?.length > 0 ? ids : null,
    };

    try {
      const response = await coreApi.get("building-units/for-initial-invoices", { params });
      setBuildingUnits(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    const delayDebounceFn = setTimeout(() => {
      if(!isMounted) return;

      (async () => {
        if (searchTerm.length > searchTermMinLength) {
          await fetchBuildingUnits({ term: searchTerm });
        }
      })();
    }, 1000);

    return () => {
      isMounted = false;
      clearTimeout(delayDebounceFn);
    }
  }, [searchTerm]);

  useEffect(() => {
    (async () => {
      await fetchBuildingUnits({ ids: selectedBuildingUnits });
    })();
  }, []);

  useEffect(() => {
    invoiceForm.resetFields(["summary"]);
  }, [buildingUnitsSummary]);

  useEffect(() => {
    invoiceForm.resetFields(["buildingUnits"]);
  }, [buildingUnitsList]);

  useEffect(() => {
    prepareBuildingUnits();
  }, [selectedBuildingUnits]);

  const prepareBuildingUnits = () => {
    const filledBuildingUnits = invoiceForm.getFieldValue("buildingUnits") || [];

    const units = selectedBuildingUnits?.map(item => {

      let unit = null;

      if (typeof item === "object" && item !== null) {
        unit = { ...item };
      }

      if (typeof item === "number") {
        unit = buildingUnits.find(u => u.id === item) || buildingUnitsList.find(u => u.id === item);
      }

      if (!unit) return null;

      unit.cleanings_start_from = moment();
      // Cleaning days from API belong to the previous partner's rules, so they must not be prefilled.
      // Keep only the days the user has already picked in this form.
      unit.cleaning_days = filledBuildingUnits.find(u => u?.id === unit.id)?.cleaning_days || [];
      unit.priceThisMonth = calculateUnitMonthlyPrice(unit, unit.cleanings_start_from);
      unit.orderPrice = getLastOrderPrice(unit);

      return unit;
    }).filter(Boolean);

    setBuildingUnitsList(units);
  };

  const getActiveWeeklyRrule = (unit, date = moment()) => {
    const fromBlueprint = unit.cleaning_service_groups?.find(group =>
      group.rrule_blueprint?.includes("FREQ=WEEKLY") &&
      group.service_rrules?.some(rrule =>
        moment(rrule.date_from).isSameOrBefore(date, "day") &&
        (!rrule.date_to || moment(rrule.date_to).isSameOrAfter(date, "day"))
      )
    );

    if (fromBlueprint) return {
      group: fromBlueprint,
      rrule: fromBlueprint.rrule_blueprint,
    };

    const groupWithServiceRrule = unit.cleaning_service_groups?.find(group =>
      group.service_rrules?.some(rrule =>
        rrule.rrule?.includes("FREQ=WEEKLY") &&
        moment(rrule.date_from).isSameOrBefore(date, "day") &&
        (!rrule.date_to || moment(rrule.date_to).isSameOrAfter(date, "day"))
      )
    );

    if (groupWithServiceRrule) {
      const r = groupWithServiceRrule.service_rrules.find(rrule =>
        rrule.rrule?.includes("FREQ=WEEKLY") &&
        moment(rrule.date_from).isSameOrBefore(date, "day") &&
        (!rrule.date_to || moment(rrule.date_to).isSameOrAfter(date, "day"))
      );
      return {
        group: groupWithServiceRrule,
        rrule: r?.rrule,
      };
    }

    // fallback: group with blueprint but no service_rrules, and order exists
    const fallbackBlueprint = unit.cleaning_service_groups?.find(group =>
      group.rrule_blueprint?.includes("FREQ=WEEKLY") &&
      (group.service_rrules?.length === 0 || !group.service_rrules)
    );

    if (fallbackBlueprint && unit.building?.active_order?.price > 0) {
      return {
        group: fallbackBlueprint,
        rrule: fallbackBlueprint.rrule_blueprint,
      };
    }

    return null;
  };

  const getAnyWeeklyRrule = (unit) => {
    const fromBlueprint = unit.cleaning_service_groups?.find(group =>
      group.rrule_blueprint?.includes("FREQ=WEEKLY") &&
      group.service_rrules?.some(rrule => !!rrule.rrule?.includes("FREQ=WEEKLY"))
    );

    if (fromBlueprint) {
      return {
        group: fromBlueprint,
        rrule: fromBlueprint.rrule_blueprint,
      };
    }

    const groupWithServiceRrule = unit.cleaning_service_groups?.find(group =>
      group.service_rrules?.some(rrule =>
        rrule.rrule?.includes("FREQ=WEEKLY")
      )
    );

    if (groupWithServiceRrule) {
      const r = groupWithServiceRrule.service_rrules.find(rrule =>
        rrule.rrule?.includes("FREQ=WEEKLY")
      );
      return {
        group: groupWithServiceRrule,
        rrule: r?.rrule,
      };
    }

    const fallbackBlueprint = unit.cleaning_service_groups?.find(group =>
      group.rrule_blueprint?.includes("FREQ=WEEKLY") &&
      (!group.service_rrules || group.service_rrules.length === 0)
    );

    if (fallbackBlueprint) {
      return {
        group: fallbackBlueprint,
        rrule: fallbackBlueprint.rrule_blueprint,
      };
    }

    return null;
  };

  const calculateUnitMonthlyPrice = (unit, cleaningsStartFrom = moment()) => {
    const buildingUnitsCount = unit?.building?.building_units?.length || 1;

    const orderPrice = getLastOrderPrice(unit);

    let activeRrule = getActiveWeeklyRrule(unit, cleaningsStartFrom);

    if (!activeRrule && orderPrice > 0) {
      activeRrule = getAnyWeeklyRrule(unit);
    }

    if (!activeRrule) return 0;

    return buildingUnitsCount > 0
      ? (orderPrice / buildingUnitsCount)
      : orderPrice;
  };

  const getLastOrderPrice = (unit) => {
    return parseFloat(
      unit?.building?.active_order?.price ??
      unit?.building?.orders?.[0]?.price ??
      0
    );
  };

  const getOrderCurrency = (unit) => {
    return (
      unit?.building?.active_order?.currency_iso_4217 ||
      unit?.building?.orders?.[0]?.currency_iso_4217 ||
      "CZK"
    );
  };

  const getCleaningsDatesForMonth = ({ rruleString, dateStart, cleaningDays }) => {
    const ruleParsed = rrulestr(rruleString);

    const start = moment(dateStart).toDate();
    const end = moment(dateStart).endOf("month").toDate();

    let byweekday = cleaningDays?.length ? cleaningDays : ruleParsed.origOptions.byweekday;

    let rule = new RRule({
      ...ruleParsed.origOptions,
      byweekday,
      dtstart: start,
    });

    let dates = rule.between(start, end, true);

    if (dates.length === 0 && cleaningDays?.length) {
      rule = new RRule({
        ...ruleParsed.origOptions,
        byweekday: ruleParsed.origOptions.byweekday,
        dtstart: start,
      });

      dates = rule.between(start, end, true);
    }

    return dates;
  };

  /**
   * Prepare summary for displaying.
   */
  const showSummary = () => {
    let buildingUnits = invoiceForm.getFieldsValue("buildingUnits")?.buildingUnits;

    buildingUnits.forEach((buildingUnit) => {
      let active = getActiveWeeklyRrule(buildingUnit, buildingUnit?.cleanings_start_from);

      if (!active) {
        active = getAnyWeeklyRrule(buildingUnit);
      }

      if (!active?.rrule) return;

      const ruleParsed = rrulestr(active.rrule);
      const subtractDays = active.group.name && active.group.name.includes("14 DNÍ") ? "30" : "60";

      let newCleaningsStartDate = buildingUnit?.cleanings_start_from;

      if (active.rrule?.includes("FREQ=WEEKLY") && active.rrule?.includes("INTERVAL=2")) {
        if (biweeklyType[buildingUnit?.id] && ["odd", "even"].includes(biweeklyType[buildingUnit?.id])) {
          const weekNumber = moment(newCleaningsStartDate).isoWeek();

          const shouldChangeStartDate =
            (biweeklyType[buildingUnit?.id] === "odd" && weekNumber % 2 === 0) ||
            (biweeklyType[buildingUnit?.id] === "even" && weekNumber % 2 !== 0);

          if (shouldChangeStartDate) {
            const daysToAdd = 7 - moment(newCleaningsStartDate).isoWeekday() + 1;
            newCleaningsStartDate = moment(newCleaningsStartDate).add(daysToAdd, 'days');
          }
        }
      }

      // Get potential rules for current month
      let potentialRule = new RRule({
        ...ruleParsed.origOptions,
        byweekday: buildingUnit?.cleaning_days?.length
          ? buildingUnit.cleaning_days
          : ruleParsed.origOptions.byweekday,
        dtstart: moment(newCleaningsStartDate).subtract(subtractDays, "days").toDate(),
      });

      // Get all potential cleaning dates for current month
      let potentialCleaningsDates = potentialRule.between(
        moment(newCleaningsStartDate).startOf("month").toDate(),
        moment(newCleaningsStartDate).endOf("month").toDate(),
        true
      );

      const newCleaningsDates = getCleaningsDatesForMonth({
        rruleString: active.rrule,
        dateStart: newCleaningsStartDate,
        cleaningDays: buildingUnit?.cleaning_days,
      });

      buildingUnit.newCleanings = newCleaningsDates;

      const basePrice = parseFloat(calculateUnitMonthlyPrice(buildingUnit, buildingUnit.cleanings_start_from));

      buildingUnit.priceThisMonth = potentialCleaningsDates.length > 0
        ? (basePrice * (newCleaningsDates.length / potentialCleaningsDates.length))
        : "0.00";
    });

    setBuildingUnitsSummary(buildingUnits);
  };

  /**
   * Handle submit of the form.
   */
  const submitForm = () => {
    if (!internalPartner?.id) {
      notification.error({
        message: t("partners.validation.selectPartner")
      });
      return;
    }

    setLoading(true);

    coreApi.post("/cleanings/recurring/mass-during-month", {
      building_units: buildingUnitsSummary.filter(unit => unit.priceThisMonth > 0 && unit.newCleanings?.length),
      partner_id: internalPartner?.id,
    })
      .then((response) => {
        notification.success({
          message: "Faktury byly úspěšně vytvořené.",
        });

        setBuildingUnits([]);
        setBuildingUnitsList([]);
        setBuildingUnitsSummary([]);
        invoiceForm.resetFields();
        setIsModalVisible(false);
      })
      .catch((error) => {
        throwError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFieldsChange = (_, allFields) => {
    setTimeout(() => {
      const cleaningsStartFromFields = document.querySelectorAll(
        'input[id^="buildingUnits_"][id$="_cleanings_start_from"]'
      );
      const cleaningDaysFields = document.querySelectorAll(
        'input[id^="buildingUnits_"][id$="_cleaning_days"]'
      );

      const areFieldsValid = Array.from(cleaningsStartFromFields).every(
        (field) => field.value
        ) && Array.from(cleaningDaysFields).every((field) => {
        const selectionOverflow = field.closest('.ant-select-selection-overflow');
        if (selectionOverflow) {
          const children = selectionOverflow.querySelectorAll('.ant-select-selection-overflow-item');
          return Array.from(children).some(child =>
            !child.classList.contains('ant-select-selection-overflow-item-suffix')
          );
        }
        return false
      });

      setIsButtonDisabled(!areFieldsValid);
    }, 300);
  };

  function isBiweekly(buildingUnit, key = null) {
    if (key !== null && invoiceForm?.getFieldsValue("buildingUnits")?.buildingUnits?.[key]) {
      buildingUnit = invoiceForm?.getFieldsValue("buildingUnits").buildingUnits[key]
    }
    return invoiceForm?.getFieldsValue("buildingUnits") && IsBiweeklyBuildingUnit(buildingUnit);
  }

  function isBiweeklyExists() {
    const units = Object.values(invoiceForm?.getFieldsValue("buildingUnits") || {});
    return units.some(unit => {
      return unit ? isBiweekly(unit[0]) : false
    });
  }

  const formatPricePerCleaning = (unit) => {
    const currency = getOrderCurrency(unit);
    const total = parseFloat(unit?.priceThisMonth || 0);
    const count = unit?.newCleanings?.length || 1;
    const precision = currency === "EUR" ? 2 : 0;
    return (total / count).toFixed(precision);
  };

  const formatMonthlyTotal = (unit) => {
    const currency = getOrderCurrency(unit);
    const total = parseFloat(unit?.priceThisMonth || 0);
    const precision = currency === "EUR" ? 2 : 0;
    return total.toFixed(precision);
  };

  return (
    <>
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
        {t("finance.invoices.addMultipleInitialInvoice")}
      </Button>

      {isModalVisible && <Modal
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          setSearchTerm("");
        }}
        width={1000}
        title={"Vytvořit faktury v průběhu mesíce"}
        footer={[
          <Button
            key="finish"
            onClick={submitForm}
            loading={loading}
            type="primary"
            disabled={buildingUnitsSummary.length <= 0 && true}
          >
            Potvrdit a vytvořit faktury v průběhu měsíce
          </Button>,
        ]}
      >
        {/* Render form */}
        <Form form={invoiceForm} initialValues={{ buildingUnits: buildingUnitsList, summary: buildingUnitsSummary }} onFieldsChange={handleFieldsChange}>
          <Row gutter={[8, 8]}>
            <Col span={24}>
              {!partner && (
                <PartnerSelect
                  name="partner_id"
                  allowClear={false}
                  required={true}
                  showSearch={true}
                  validationMessage={t("partners.validation.selectPartner")}
                  onChange={(value) => {
                    setInternalPartner(value);
                  }}
                />
              )}
            </Col>
            <Col span={24}>
              <Select
                mode="multiple"
                allowClear
                style={{
                  width: "100%",
                }}
                placeholder={t("buildings.selectUnits")}
                onChange={(e) => {
                  setSelectedBuildingUnits(e)
                  handleFieldsChange()
                }}
                onSearch={(e) => {
                  setSearchTerm(e);
                }}
                filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
              >
                {buildingUnits?.map((buildingUnit) => {
                  return (
                    <Option value={buildingUnit?.id} key={buildingUnit?.id}>
                      {buildingUnit?.street + " " + buildingUnit?.house_number + " " + buildingUnit?.city + "(" + buildingUnit?.building_id + ")"}
                    </Option>
                  );
                })}
              </Select>
            </Col>
          </Row>

          {buildingUnitsList.length > 0 && <Divider />}

          <Row gutter={[8, 8]}>
            {buildingUnitsList.length > 0 && (
              <Col span={24}>
                <Row gutter={[12, 12]}>
                  <Col span={4}>
                    <strong>Název vchodu</strong>
                  </Col>

                  <Col span={4}>
                    <strong>Cena z objednávky</strong>
                  </Col>

                  <Col span={3}>
                    <strong>Frekvence</strong>
                  </Col>

                  <Col span={4}>
                    <strong>Začátek úklidu</strong>
                  </Col>

                  <Col span={isBiweeklyExists() ? 5 : 9}>
                    <strong>Úklidové dny</strong>
                  </Col>
                  {isBiweeklyExists() &&
                  <Col span={4}>
                    <strong>{t("schedules.biweeklyType")}</strong>
                  </Col>}
                </Row>
              </Col>
            )}

            <Col span={24}>
              <Form.List name="buildingUnits">
                {(fields) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row gutter={[12, 12]} key={key}>
                        <Col span={4}>
                          <Form.Item>
                            <Input
                              disabled={true}
                              bordered={false}
                              value={
                                invoiceForm?.getFieldsValue("buildingUnits")?.buildingUnits?.[key]?.street +
                                " " +
                                invoiceForm?.getFieldsValue("buildingUnits")?.buildingUnits?.[key]?.house_number
                              }
                            />
                          </Form.Item>
                        </Col>

                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "orderPrice"]}
                            fieldKey={(restField.key, "orderPrice")}
                          >
                            <Input disabled={true} bordered={false} />
                          </Form.Item>
                        </Col>

                        <Col span={3}>
                          <Form.Item>
                            <Input
                              disabled={true}
                              bordered={false}
                              value={
                                invoiceForm
                                  ?.getFieldsValue("buildingUnits")
                                  ?.buildingUnits?.[key]?.cleaning_service_groups?.filter((cleaningServiceGroup) => {
                                    return cleaningServiceGroup?.rrule_blueprint?.includes("WEEKLY");
                                  })[0]?.name
                              }
                            />
                          </Form.Item>
                        </Col>

                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "cleanings_start_from"]}
                            fieldKey={(restField.key, "cleanings_start_from")}
                            required
                          >
                            <DatePicker />
                          </Form.Item>
                        </Col>

                        <Col span={isBiweekly(buildingUnits?.[key], key) ? 5 : 9}>
                          <Form.Item
                            {...restField}
                            name={[name, "cleaning_days"]}
                            fieldKey={(restField.key, "cleaning_days")}
                          >
                            <Select
                              mode="multiple"
                              allowClear
                              style={{
                                width: "100%",
                              }}
                              placeholder="Úklidové dny"
                            >
                              {days.map((day) => {
                                return (
                                  <Select.Option key={day} value={day}>
                                    {t("rrules.byweekday." + day)}
                                  </Select.Option>
                                );
                              })}
                            </Select>
                          </Form.Item>
                        </Col>
                        {isBiweekly(buildingUnits?.[key], key) &&
                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "biweeklyType"]}
                            fieldKey={(restField.key, "biweekly_type")}
                          >
                            <BiweeklyTypeSelect
                              label={''}
                              postChange={(value) => {
                                let val = biweeklyType
                                val[buildingUnits?.[key].id] = value;
                                setBiweeklyType(val)
                              }}
                            />
                          </Form.Item>
                        </Col>}
                      </Row>
                    ))}
                  </>
                )}
              </Form.List>

              {buildingUnitsList.length > 0 && (
                <Col span={12} style={{ float: "right", marginBottom: "24px" }}>
                  <Button onClick={() => showSummary()} type={"primary"} disabled={isButtonDisabled}>
                    Zobrazit shrnutí a pokračovat
                  </Button>
                </Col>
              )}

              {buildingUnitsSummary.length > 0 && <Divider />}

              {buildingUnitsSummary.length > 0 && (
                <Form.List name="summary">
                  {(fields) => (
                    <Card title="Shrnutí vchodů">
                      {fields.map(({ key, name, ...restField }) => (
                        <Col span={24} key={key} style={{ marginBottom: "12px" }}>
                          <Card
                            type="inner"
                            size="small"
                            style={{ width: "100%" }}
                            title={
                              <Link to={`/buildings/${invoiceForm?.getFieldValue("summary")?.[key]?.building?.id}/unit/${invoiceForm?.getFieldValue("summary")?.[key]?.id}`} target={"_blank"}>
                                {invoiceForm?.getFieldValue("summary")?.[key]?.street + " " + invoiceForm?.getFieldValue("summary")?.[key]?.house_number}
                              </Link>
                            }
                          >
                            {parseFloat(invoiceForm?.getFieldValue("summary")?.[key]?.priceThisMonth) === 0 && (
                              <p style={{ color: "red" }}>
                                ⚠ Tento vchod nemá aktivní pravidlo pro úklid ve zvoleném období. Faktura nebude vygenerována.
                              </p>
                            )}

                            {invoiceForm?.getFieldValue("summary")?.[key]?.newCleanings?.map((cleaning, i) => {
                              return (
                                <div key={i + key}>
                                  <p>
                                    {i + 1}. Úklid proběhne <strong>{moment(cleaning)?.format("DD.MM.YYYY")}</strong> za
                                    cenu{" "}
                                    <strong>
                                      {new Intl.NumberFormat("cs-CZ", {
                                        style: "currency",
                                        currency: getOrderCurrency(invoiceForm?.getFieldValue("summary")?.[key]),
                                      }).format(
                                        formatPricePerCleaning(invoiceForm?.getFieldValue("summary")?.[key])
                                      )}
                                    </strong>
                                  </p>
                                </div>
                              );
                            })}

                            <hr />

                            <p>
                              Celkem za tento měsíc{" "}
                              <strong>
                                {new Intl.NumberFormat("cs-CZ", {
                                  style: "currency",
                                  currency: getOrderCurrency(invoiceForm?.getFieldValue("summary")?.[key]),
                                }).format(
                                  formatMonthlyTotal(invoiceForm?.getFieldValue("summary")?.[key])
                                )}
                              </strong>
                            </p>
                          </Card>
                        </Col>
                      ))}
                    </Card>
                  )}
                </Form.List>
              )}
            </Col>
          </Row>
        </Form>
      </Modal>}
    </>
  );
};

export default MassInvoiceDuringMonthForm;
